import log from "loglevel"
import { API, NexusResponse, NexusSuccessResponse } from "../../api/API"
import {
    EventRegexTemplate,
    FlightlineEvent,
    FlightlineEventDTO,
    regexTemplateOptions,
} from "../../models/FlightlineEvent"
import { MissionShippingInfoWrapper } from "../../models/missions/ShippingInfo"
import { compactFlownDate } from "../../tools"
import { EventPlannedLine, parseFlightlineEventNames, unplannedLineValue } from "../../utils/FlightlineEventUtils"
import { MissionUtils } from "../../utils/MissionUtils"
import { StringUtils } from "../../utils/StringUtils"
import { useAppErrorStore } from "../../zstore/AppErrorStore"
import { useFlightlineEventStore } from "../../zstore/FlightlineEventStore"
import { DispatchNexus } from "../DispatchNexus"
import { CreatedFlightPlannedLine, FlightplanType } from "../types/FlightPlansTypes"
import {
    AirportLists,
    AllEmployeeTypes,
    EmployeeType,
    FlightlineStatusOption,
    MissionGetDto,
    MissionNameType,
    MissionParentPutDto,
    MissionType,
    ParsedMissionType,
    PlatformList,
} from "../types/MissionsTypes"
import { Types } from "../types/Types"

export async function fetchMission(dispatch: DispatchNexus, missionId: string): Promise<void> {
    dispatch({ type: Types.Missions_FetchMission })

    try {
        const result: NexusSuccessResponse<MissionGetDto> = await API.get(
            `/mission/${missionId}`,
            "Error retrieving mission",
        )
        const selectedMission = MissionUtils.transformMissionDTOToMission(result.data)

        dispatch({
            type: Types.Missions_FetchMissionSuccess,
            payload: { selectedMission },
        })
    } catch (error) {
        log.error(error)
        dispatch({
            type: Types.Missions_UpdateError,
            payload: { error: error.message },
        })
    }
}

export async function updateMission(
    dispatch: DispatchNexus,
    mission: MissionType,
    flightlineEvents: FlightlineEvent[],
    shippingInfo: MissionShippingInfoWrapper[],
    isNoFile: boolean,
    isVendorFlow: boolean,
    isNoFlight: boolean,
): Promise<boolean> {
    try {
        // check for missing flightplan
        if (flightlineEvents.length > 0) {
            flightlineEvents.forEach((ev) => {
                if (ev.flight_status !== "Ignored" && ev.fe_planned_line_ids.length === 0) {
                    throw Error(`Cannot submit mission due to missing flight planned line: (${ev.line_original_name}).\
                     Please verify the event is not ignored.`)
                }
            })
        }
        // no-file flow but no events
        if (isNoFile && flightlineEvents.length === 0) {
            throw Error(
                "Cannot submit mission due to missing flightline events. Please import flightplanned lines using the import tool in the Flightline Review tab.",
            )
        }

        const missionPutPayload: MissionParentPutDto = MissionUtils.transformMissionToMissionDTO(
            mission,
            flightlineEvents,
            shippingInfo,
            { isVendorFlow, isNoFlight, isNoFile },
        )

        await API.put(`/mission/${mission.mission_id || 0}`, missionPutPayload, "Couldn't update mission.")
        return true
    } catch (error) {
        log.error(error)
        dispatch({
            type: Types.Missions_UpdateError,
            payload: { error: error.message },
        })
    }
    return false
}

// @Todo Change to Generic - similar to delete selectedflightplanlist
export async function deleteMission(dispatch: DispatchNexus, mission_id: number): Promise<boolean> {
    dispatch({ type: Types.Missions_DeleteMission })

    try {
        await API.delete(`/mission/${mission_id}`, "Error deleting mission.")

        dispatch({ type: Types.Missions_DeleteMissionSuccess })
        return true
    } catch (error) {
        log.error(error)
        dispatch({ type: Types.Missions_DeleteMissionError })
        useAppErrorStore.getState().setAppError(error.message)
    }
    return false
}

export const getEventsForMission = async (missionId: string) => {
    const result: {
        data: {
            flightline_events: FlightlineEventDTO[]
            flight_plans: FlightplanType[] | null
        }
    } = await API.get(`/mission/${missionId}/flightline-events`, "Error retrieving flightline events")

    const events: FlightlineEvent[] = result.data.flightline_events.map((event, index): FlightlineEvent => {
        const fe_planned_line_ids = event.planned_line_ids.length === 0 ? [unplannedLineValue] : event.planned_line_ids
        return {
            ...event,
            fe_event_id: index + 1,
            fe_planned_line_ids,
            sensor_details: event.sensor_details
                ? StringUtils.stringifyJSON(StringUtils.parseJSON(event.sensor_details))
                : null,
            node_events: event.node_events?.sort((a, b) => (a.node_original_name > b.node_original_name ? 1 : -1)),
        }
    })

    /*
     * If there are flightline events but there are no flightplans
     * result.data returns null - should only happen during testing if all lines are UL
     */
    if (events.length > 0 && !result.data.flight_plans) {
        throw Error("Error retrieving flightplan.")
    }

    return {
        flightline_events: events,
        flight_plans: result.data.flight_plans ?? [],
    }
}

export const getFlightPlannedLines = async (flight_plan_ids: number[]): Promise<CreatedFlightPlannedLine[]> => {
    const _flight_plan_ids = flight_plan_ids.map((flight_plan) => {
        return `plan_id=${String(flight_plan)}`
    })
    const _url = _flight_plan_ids.join("&")

    const result: {
        data: { flightplanned_lines: CreatedFlightPlannedLine[] }
    } = await API.get(
        `/mission/flightplanned-lines?${_url}`,
        "Error retrieving flightplanned lines. This means flightline events cannot be imported on the final tab.",
    )

    return result.data.flightplanned_lines
}

export async function parseSensorFile(
    dispatch: DispatchNexus,
    sensorFiles: File[],
    project_number: string,
    eventPlannedLines: EventPlannedLine[],
) {
    useAppErrorStore.getState().clear()
    dispatch({ type: Types.Missions_FetchParsedMissions })

    try {
        const formData = new FormData()
        sensorFiles.forEach((file) => {
            formData.append("file", file)
        })

        const response: NexusResponse<
            {
                missions: {
                    serial_number_1: string
                    serial_number_2: string
                    flown_date: string
                    flight_num: number
                    channel_name: string | null // can be null or missing for file formats without multi-channel data
                    source_file: string
                    flightline_events: FlightlineEventDTO[]
                    flight_notes: string
                    sensor_notes: string
                }[]
                warnings: string[]
            }[]
        > = await API.uploadFile("/upload/sensor", formData)
        const result: NexusSuccessResponse<MissionNameType[]> = await API.get(
            `/missions/${project_number}/names`,
            `Couldn't retrieve mission names for project number ${project_number}`,
        )

        const missionNames = result.data

        // The previous code referenced a collected warnings field that didn't exist
        const allWarnings = [...(response.data.flatMap((sensorFileUploadData) => sensorFileUploadData.warnings) || [])]

        const allFileMissionsArray = response.data
        if (!allFileMissionsArray.length) {
            throw new Error("Backend response was not a mission array.")
        }

        const missionDict: { [key: string]: Omit<ParsedMissionType, "mission_name"> } = {}

        // In searching for the best regex to use, we use whatever regex works best with the first file
        // TODO: for each file should there only be one 'bestRegex'?
        let bestRegex: EventRegexTemplate | undefined
        allFileMissionsArray.forEach(({ missions: fileMissionArray, warnings }) => {
            allWarnings.push(...warnings)

            fileMissionArray.forEach((mission) => {
                const {
                    flown_date,
                    flight_num,
                    serial_number_1,
                    serial_number_2,
                    source_file,
                    flight_notes,
                    sensor_notes,
                    channel_name,
                } = mission
                let events: FlightlineEvent[]

                if (bestRegex == null) {
                    // For the first channel or if we have no matches, yet we will determine the best regex to use, and thereafter
                    // reuse that one
                    let bestEventsResult: FlightlineEvent[] = []
                    const matchesFound: number = 0

                    regexTemplateOptions.forEach((regexTemplate) => {
                        const result = parseFlightlineEventNames(
                            mission.flightline_events,
                            regexTemplate.value,
                            eventPlannedLines,
                        )
                        const matches = result.filter((event) => event.fe_name_from_original != null).length
                        if (bestRegex == null || matchesFound < matches) {
                            bestRegex = regexTemplate
                            bestEventsResult = result
                        }
                    })

                    events = bestEventsResult
                } else {
                    events = parseFlightlineEventNames(mission.flightline_events, bestRegex.value, eventPlannedLines)
                }

                // get next flight_num if mission already exists in database
                let maxExistingFlight = 0
                const compactDate = compactFlownDate(flown_date)

                missionNames.forEach((m: MissionNameType) => {
                    if (typeof m.mission_name === "string") {
                        const [serial, date, flight] = m.mission_name.split("_")
                        if (serial === serial_number_1 && date === compactDate && flight) {
                            maxExistingFlight = Math.max(maxExistingFlight, +flight.slice(1))
                        }
                    }
                })

                // missionName will be key in missionDict: serialNumber_flownDate_flightNumber (channelName)
                const missionName = MissionUtils.getMissionName(serial_number_1, compactDate, flight_num, channel_name)

                // Merge into existing record if mission's already in dict
                if (missionDict[missionName]) {
                    const old_source_file = missionDict[missionName].source_file

                    missionDict[missionName] = {
                        ...missionDict[missionName],
                        flightline_events: [...missionDict[missionName].flightline_events, ...events],
                        source_file: old_source_file?.includes(source_file)
                            ? old_source_file
                            : `${old_source_file},${source_file}`,
                    }
                } else {
                    missionDict[missionName] = {
                        channel_name,
                        flightline_events: events,
                        flown_date,
                        flight_notes,
                        sensor_notes,
                        serial_number_1,
                        serial_number_2,
                        source_file,
                        flight_num: maxExistingFlight + flight_num,
                    }
                }
            }) //end foreach mission in file
        }) //end foreach file sent to parser

        const parsedMissions: ParsedMissionType[] = []
        for (const missionName in missionDict) {
            const newMission: ParsedMissionType = { ...missionDict[missionName], mission_name: missionName }

            // init flightline events here incase there are multiple missionDict entries with same compName
            newMission.flightline_events.forEach((fl: FlightlineEvent, index: number) => {
                fl.fe_event_id = index + 1

                // set events to Ignored status if regex could not parse flightline_name OR no matches in planned lines
                if (!fl.fe_name_from_original || !fl.fe_planned_line_ids.length) {
                    fl.flight_status = FlightlineStatusOption.IGNORED
                } else {
                    fl.flight_status = FlightlineStatusOption.NORMAL
                }
            })

            parsedMissions.push(newMission)
        }

        parsedMissions.sort((a, b) => {
            if (a.flown_date && b.flown_date && a.flown_date !== b.flown_date) {
                return a.flown_date < b.flown_date ? 1 : -1
            }
            if (a.flight_num !== b.flight_num) {
                return a.flight_num < b.flight_num ? -1 : 1
            }
            if (a.channel_name && b.channel_name) {
                return a.channel_name < b.channel_name ? -1 : 1
            } //asc channel
            return 0
        })

        useFlightlineEventStore.getState().setInitialMatchingExpression(bestRegex ?? regexTemplateOptions[0])
        dispatch({
            type: Types.Missions_FetchParsedMissionsSuccess,
            payload: { parsedMissions, warnings: allWarnings },
        })
        return parsedMissions
    } catch (error) {
        log.error(error)
        let errorMessage = error.message
        if (error.message === "413") {
            errorMessage = "File too large (1GB max)."
        }
        useAppErrorStore.getState().setAppError(errorMessage)
    }
}

export const getEmployeeListByProject = async (projectNumber: string) => {
    const result: NexusSuccessResponse<AllEmployeeTypes> = await API.get(
        `/employees/${projectNumber}/mission`,
        "Error retrieving employee list",
    )

    if (result.data?.employees) {
        // sort by pref name
        result.data.employees = result.data.employees.sort((a: EmployeeType, b: EmployeeType) => {
            return a.employee_full_name > b.employee_full_name ? 1 : -1
        })
    }

    return result
}

export async function fetchEmployeeList(dispatch: DispatchNexus, projectNumber: string): Promise<void> {
    dispatch({ type: Types.Missions_FetchEmployeeList })

    try {
        const result = await getEmployeeListByProject(projectNumber)

        dispatch({
            type: Types.Missions_FetchEmployeeListSuccess,
            payload: { employeeList: result.data },
        })
    } catch (error) {
        log.error(error)
        dispatch({
            type: Types.Missions_UpdateError,
            payload: { error: error.message },
        })
    }
}

export async function fetchVendorNameList(dispatch: DispatchNexus): Promise<void> {
    dispatch({ type: Types.Missions_FetchVendorNameList })

    try {
        const result: NexusSuccessResponse<string[]> = await API.get(
            "/vendor-names",
            "Error retrieving vendor name list.",
        )

        dispatch({
            type: Types.Missions_FetchVendorNameListSuccess,
            payload: { vendorNameList: result.data },
        })
    } catch (error) {
        log.error(error)
        dispatch({
            type: Types.Missions_UpdateError,
            payload: { error: error.message },
        })
    }
}

export async function fetchPlatformList(dispatch: DispatchNexus, projectNumber: string): Promise<void> {
    dispatch({ type: Types.Missions_FetchPlatformList })

    try {
        const result: NexusSuccessResponse<PlatformList> = await API.get(
            `/platforms/${projectNumber}`,
            "Error retrieving platform list",
        )

        dispatch({
            type: Types.Missions_FetchPlatformListSuccess,
            payload: result.data,
        })
    } catch (error) {
        log.error(error)
        dispatch({
            type: Types.Missions_UpdateError,
            payload: { error: error.message },
        })
    }
}

export async function fetchAirportList(dispatch: DispatchNexus, projectNumber: string): Promise<void> {
    dispatch({ type: Types.Missions_FetchAirportList })

    try {
        const result: NexusSuccessResponse<AirportLists> = await API.get(
            `/airports/${projectNumber}`,
            "Error retrieving airport list",
        )

        const airportList = result.data.airports.filter((airport) => airport !== "N/A")
        airportList.unshift("N/A")

        dispatch({
            type: Types.Missions_FetchAirportListSuccess,
            payload: { airports: airportList, project_airports: result.data.project_airports },
        })
    } catch (error) {
        log.error(error)
        dispatch({
            type: Types.Missions_UpdateError,
            payload: { error: error.message },
        })
    }
}
